import { useMutation } from "@tanstack/react-query";
import { useContext } from "react";

import { QuoteDetailsContext } from "~/features/quote/context/quote-details-context.tsx";
import { apiRoutes } from "~/shared/services/api/api-routes.ts";
import { getBinaryResponse } from "~/shared/services/api/fetch-client.ts";

type GenerateRateConfirmationParams = {
	carrierIds: string[];
};

/**
 * Hook to manage generating rate confirmation documents for carriers
 * Using useMutation for manual triggering of GET request with parameters

 */
export function useRateConfirmationGenerate() {
	const { quoteState } = useContext(QuoteDetailsContext);

	const mutation = useMutation({
		mutationFn: async ({ carrierIds }: GenerateRateConfirmationParams) => {
			const resp = await getBinaryResponse(
				generateViewUrl(carrierIds).fetchUrl,
			);

			// If the response indicates failure, throw an error to trigger TanStack Query's error state
			if (!resp.success) {
				throw new Error(resp.error || "Rate confirmation generation failed");
			}

			return resp.data;
		},
		retry: false,
		gcTime: 0, // Don't cache failed results
	});

	const generateRateConfirmation = (carrierIds: string[]) => {
		if (mutation.isPending) {
			console.warn("Rate confirmation generation already in progress");
			return;
		}
		mutation.mutate({ carrierIds });
	};

	const generateViewUrl = (carrierIds: string[]) => {
		const baseUrl = apiRoutes.GENERATERATECONFIRMATION.replace(
			":public-guid",
			String(quoteState.publicGuid),
		);

		const params = new URLSearchParams();
		for (const id of carrierIds) {
			params.append("carrierPriceIdList", id);
		}

		return {
			fetchUrl: `${baseUrl}?${params.toString()}`,
			// Use legacy URL from environment config
			fullUrl: `${globalThis._envconfig.legacy}${quoteState.publicGuid}/multiple-rates?${params.toString()}`,
		};
	};

	return {
		generateRateConfirmation,
		isLoading: mutation.isPending,
		isError: mutation.isError,
		error: mutation.error,
		data: mutation.data,
		isSuccess: mutation.isSuccess,
		reset: mutation.reset,
		generateViewUrl,
	};
}
